(ns frontend.ux-overhaul.nev.components.attachments
  (:require-macros [frontend.macros :refer [without-default-action-and-propagation]])
  (:require
   [frontend.state.current-session :as session]
   [reagent.core :as r]
   [frontend.logging :as logging]
   [common.localization :as loc]
   [frontend.actions.common :as common-actions]
   [frontend.actions.attachment :as attachment-actions]
   [frontend.actions.entity :as entity-actions]
   [frontend.utils :as fu]
   [frontend.view.components.clipboard :as clipboard]
   [frontend.view.components.upload :as upload]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.delete-confirmation.view :as delete-confirmation-modal]
   [common.schema.registry :as registry]
   [common.schema.field :as field]
   [common.url :as url]
   [potpuri.core :refer [dissoc-in]]
   [frontend.state.permissions :as f-permissions]
   [ui.dom-globals :as dg]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.enrich.existing :as enrich-existing]
   [cljs.core.async :as async]))

(defn add-indicator!
  [local-atom token file-name]
  (swap! local-atom assoc-in [:uploading token] {:file-name file-name}))

(defn remove-indicator!
  [local-atom token]
  (swap! local-atom dissoc-in [:uploading token]))

(defn read-progress
  [channel state upload-token]
  (async/take!
   channel
   (fn [progress]
     (let [{:keys [direction loaded total]} progress
           prog (/ loaded total)]
       (cond (= :download direction)
             (swap! state assoc-in [:uploading upload-token :progress] 100.0)
             (= :upload direction)
             (do (swap! state assoc-in [:uploading upload-token :progress] (* 100 (float prog)))
                 (swap! state assoc-in [:uploading upload-token :status] :uploading)))
       (when (= :upload direction)
         (read-progress channel state upload-token))))))

(defn add-attachment!
  [{:keys [local-atom success! db/id]} e]
  (let [file (aget (:files e) 0)
        file-name (.-name file)
        attachment {:filename    file-name
                    :description nil
                    :file        file
                    :target      id}
        prog-channel (async/chan)
        token-success! (fn [token]
                         (add-indicator! local-atom token file-name)
                         (read-progress prog-channel local-atom token)
                         (attachment-actions/*=>add-attachment
                          {:multipart-params (assoc attachment :token token)
                           :progress         prog-channel}
                          (fn [resp]
                            (swap! local-atom assoc-in [:uploading token :status] :complete)
                            (when success!
                              (success! (:body resp))))
                          (fn [e]
                            (logging/error "Error in uploading attachment" e)
                            (swap! local-atom assoc-in [:uploading token :status] :error))))]
    (common-actions/*<-create-op-token nil
                                       token-success!
                                       (fn [e]
                                         ;;TODO show error-bar
                                         (logging/error "resp token=" e)))))

(defn remove-attachment!
  [target-id attachment]
  (attachment-actions/*<-remove-attachment
   {:target-id target-id :attachment-id (:db/id attachment)}
   (partial logging/unimplemented "remove-attachment! Success")
   (partial logging/error "remove-attachment! failed")))

(defn upload-area
  [local-atom entity-id success! args]
  [upload/upload-zone
   (merge {:css-class  "attachments-module-upload-zone"
           :on-change! (fn [e]
                         (add-attachment! {:db/id      entity-id
                                           :success!   success!
                                           :local-atom local-atom}
                                          e))}
          args)])

(defn indicators
  [local-atom entity-id]
  [:div.upload-indicators
   (->> @local-atom
        :uploading
        (remove (fn [[token meta]]
                  (= (:status meta) :complete)))
        (map (fn [[token meta]]
               (let [{:keys [file-name progress status]} meta]
                 [:div
                  {:key        (str "uploading-indicator-" token)
                   :class-name (fu/cs "upload-indicator" status)}
                  (when (some #{status} [:error :uploading])
                    (let [progress (or progress 0.0)]
                      [:span.upload-indicator__bar
                       {:style {:width (str (.toFixed progress 2) "%")}}]))
                  [:div
                   {:class-name "grid grid-cols-12 gap-4 px-4 z-[1]"}
                   [:div
                    {:class-name "col-span-6"}
                    [:label.upload-indicator__label
                     [:span
                      (case status
                        :uploading (loc/tr :file-upload-indicator-label)
                        :error (loc/tr :file-upload-error-label)
                        "")]]]
                   [:div
                    {:class-name "col-span-6 text-right"}
                    [:span.d-flex.align-items-center.justify-content-end
                     (when (some #{status} [:error :uploading])
                       (let [progress (or progress 0.0)]
                         [:span.d-inline-block.pr-3.text-color--light-grey.text-size--xs
                          (str (.toFixed progress 2) "%")]))
                     (when (not= :uploading status)
                       [:i.fas.fa-times-circle.text-color--danger.d-inline-block.pr-1
                        {:on-click (fn [_]
                                     (swap! local-atom dissoc-in [:uploading token]))}])]]
                   [:div
                    {:class-name "col-span-12"}
                    [:span.text-size--sm file-name]]]])))

        (doall))])

(defn attachment-view
  [{:keys [attachment target-id hide-delete?]}]
  (let [{:keys [attachment/filename attachment/av-status]} attachment
        infected? (= av-status :INFECTED)]
    (when filename
      [:div.attachments-list-attachment
       (when-not infected?
         {:on-click #(fu/open-attachment! (:db/id attachment))})
       [:div.module-attachment-left
        [:i.fal.fa-paperclip.module-attachment-icon]
        [:span.module-attachment-filename filename]]
       [:div.module-attachment-right
        [:div.module-attachment-buttons
         ;; The row opens the attachment on click, so keep the robot's click here.
         ;; The robot loads the entity itself: the session's copy may be stale,
         ;; and its pipeline decides which fields the robot may fill.
         (when (and target-id (not infected?))
           [:span {:on-click dg/stop-propagation}
            [enrich-existing/start-button-for
             {:entity-id     target-id
              :attachment-id (:db/id attachment)}]])
         (when-not infected?
           [:button.module-attachment-button
            {:on-click (without-default-action-and-propagation
                        (fu/open-attachment! (:db/id attachment)))}
            [:i.fal.fa-external-link]])
         (when-not infected?
           [:button.module-attachment-button
            {:on-click (without-default-action-and-propagation
                        (fu/download-attachment! (:db/id attachment)))}
            [:i.fal.fa-cloud-download]])
         (when-not (or (session/is-read-only-user?)
                       hide-delete?)
           [:button.module-attachment-button
            {:on-click (without-default-action-and-propagation
                        (modals/open-modal!
                         delete-confirmation-modal/modal-key
                         {:body       (loc/tr :attachment-delete)
                          :on-delete! #(remove-attachment! target-id attachment)}))}
            [:i.fal.fa-trash-alt]])]]
       (when infected?
         [fu/tooltip-overlay {:id (str "infected-attachment" (:db/id attachment))}
          (loc/tr :infected-attachment-warning-tooltip)
          [:span.infected (loc/tr :infected-attachment-warning)]])])))

(defn open-url-tool
  [value]
  (when (seq value)
    [{:icon     :tool-item/open-url
      :tooltip  (loc/tr :open)
      :on-click (fn [_]
                  (when-let [sanitized (fu/sanitize-url
                                        (url/relative-or-protocol-url (str value) :https))]
                    (.open js/window sanitized "_blank")))}]))

(defn network-drive
  [{:keys [_entity-id _entity-atom]}]
  (let [field-schema (registry/get-field-schema @session/tenant-schema :common/network-drive)
        local (r/atom {:dirty? false :value nil})]
    (fn [{:keys [_entity-id entity-atom]}]
      (let [entity @entity-atom
            stored-value (:common/network-drive entity)
            current (if (:dirty? @local) (:value @local) stored-value)
            read-only?
            (not (f-permissions/field-write-access? :common/network-drive
                                                    (:entity/type @entity-atom)
                                                    (fu/pipeline-id @entity-atom)))]
        [labelled-input/with-actions
         {:tools (concat (clipboard/copy-tool current nil)
                         (open-url-tool current))}
         [labelled-input/view
          {:data-type   :data.type/text-url
           :value       current
           :label       (or (->> field-schema :field/translations :en)
                            (loc/tr :common/network-drive))
           :read-only?  read-only?
           :disabled?   read-only?
           :placeholder (field/placeholder field-schema)
           :on-change   (fn [new-value]
                          (reset! local {:dirty? true :value new-value}))
           :on-blur     (fn [_]
                          (let [new-value (if (:dirty? @local) (:value @local) stored-value)]
                            (when-not (= new-value stored-value)
                              (swap! entity-atom assoc :common/network-drive new-value)
                              (entity-actions/*->field-update!
                               (:db/id entity)
                               :common/network-drive
                               new-value
                               (fn [_]
                                 (reset! local {:dirty? false :value nil}))
                               (fn [err]
                                 (when (= new-value (get @entity-atom :common/network-drive))
                                   (swap! entity-atom assoc :common/network-drive stored-value))
                                 (logging/error "network-drive update failed:" err))))))}]]))))

(defn render
  [local-atom {:keys [entity-atom entity-id attachments] :as args}]
  (let [show-network-drive? (f-permissions/field-read-access?
                             :common/network-drive
                             (:entity/type @entity-atom)
                             (fu/pipeline-id @entity-atom))
        attachments (->> attachments
                         (remove nil?)
                         (sort-by :attachment/created)
                         reverse)]
    [:div
     (when show-network-drive?
       [network-drive {:entity-id entity-id :entity-atom entity-atom}])
     [:div.attachment-list
      (doall
       (for [attachment attachments]
         ^{:key (str "attachment-" entity-id
                     "-" (or (:db/id attachment)
                             attachment))}
         [attachment-view
          {:attachment attachment
           :target-id  entity-id}]))]
     [indicators local-atom entity-id]
     (when-not (session/is-read-only-user?)
       [upload-area local-atom entity-id identity {:lang-kw :en}])]))

(defn view
  [{:keys [:entity-id]}]
  (let [local-atom (r/atom {})
        entity-atom (r/cursor session/entities [entity-id])]
    (r/create-class
     {:reagent-render (fn [args]
                        (render local-atom
                                (merge args
                                       {:entity-atom entity-atom})))})))
